import { useState, useEffect } from "react";
import { ShieldCheck, RefreshCw, ArrowLeft } from "lucide-react";

const API = "/api"; // same-origin — the ingress/worker proxies /api to the backend

const Council = () => {
  const [code, setCode] = useState(() => sessionStorage.getItem("council_code") || "");
  const [events, setEvents] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (accessCode) => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/chat-events`, {
        headers: { "X-Council-Code": accessCode },
      });
      if (res.status === 401) {
        setError("Incorrect access code.");
        sessionStorage.removeItem("council_code");
        setEvents(null);
        return;
      }
      if (!res.ok) throw new Error("request failed");
      const data = await res.json();
      setEvents(data.events);
      setTotal(data.total);
      sessionStorage.setItem("council_code", accessCode);
    } catch {
      setError("Could not reach the server. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (code.trim()) load(code.trim());
  };

  useEffect(() => {
    const stored = sessionStorage.getItem("council_code");
    if (stored && events === null) load(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (events === null && !sessionStorage.getItem("council_code")) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center px-6">
        <div className="w-full max-w-sm p-8 rounded-2xl bg-stone-900/80 border border-stone-800 space-y-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-amber-500" size={22} />
            <div>
              <h1 className="text-lg font-bold display-serif">Council Access</h1>
              <p className="mono-tag text-[10px] uppercase tracking-wider text-stone-400">
                Private · Directors only
              </p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Council access code"
              aria-label="Council access code"
              data-testid="council-code-input"
              className="w-full rounded-lg bg-stone-950 border border-stone-700 px-3 py-2.5 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-amber-600/60"
            />
            {error && (
              <p data-testid="council-error" className="text-xs text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy || !code.trim()}
              data-testid="council-login-button"
              className="w-full px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-stone-950 font-bold text-sm transition-colors"
            >
              {busy ? "Verifying…" : "Enter"}
            </button>
          </form>
          <a
            href="/"
            data-testid="council-back-link"
            className="flex items-center gap-2 text-xs text-stone-400 hover:text-amber-400 transition-colors"
          >
            <ArrowLeft size={14} /> Back to the Institute page
          </a>
        </div>
      </div>
    );
  }

  if (events === null) {
    return null; // auto-loading with stored code
  }

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500">
              Council Records
            </span>
            <h1 className="text-2xl sm:text-3xl font-bold display-serif mt-1">
              Visitor Question Log
            </h1>
            <p className="text-xs text-stone-400 mt-1" data-testid="question-log-count">
              {total} total questions asked · showing latest {events.length}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(sessionStorage.getItem("council_code"))}
              data-testid="question-log-refresh"
              className="flex items-center gap-2 px-3 py-2 rounded border border-stone-700 text-stone-300 hover:border-amber-600/60 hover:text-amber-300 text-xs transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <a
              href="/"
              data-testid="question-log-back-link"
              className="text-xs text-stone-400 hover:text-amber-400 transition-colors"
            >
              Back to site
            </a>
          </div>
        </div>

        {events.length === 0 ? (
          <div
            data-testid="question-log-empty"
            className="p-8 rounded-2xl bg-stone-900/60 border border-stone-800 text-center text-sm text-stone-400"
          >
            No visitor questions yet. They will appear here as soon as someone uses the
            "Ask the Institute" assistant.
          </div>
        ) : (
          <div className="space-y-4" data-testid="question-log-list">
            {events.map((ev, i) => (
              <div
                key={i}
                data-testid={`question-log-entry-${i}`}
                className="p-5 rounded-xl bg-stone-900/60 border border-stone-800 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="mono-tag text-[10px] uppercase tracking-wider text-amber-400/90">
                    Question
                  </span>
                  <span className="text-[10px] text-stone-500 font-mono">
                    {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ""}
                  </span>
                </div>
                <p className="text-sm text-stone-100 font-medium">{ev.question}</p>
                <p className="text-xs text-stone-400 leading-relaxed border-t border-stone-800/70 pt-3">
                  {ev.answer}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Council;
